import { ExternalLink, Plus } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";
import { ReactNode, useMemo, useState } from "react";
import { Yacht } from "../../Yachts/model";
import { useOffersStore } from "../../../stores/offersStore";
import { calculateCurrencyPrice } from "../../../lib/utils";
import { Currency } from "../../Currencies/model";
import { getConfig } from "@/src/config"; // Import runtime config
import YachtLabel from "@/src/components/ui/yacht-label";
import { format } from "date-fns";
import { CharterFlag, CardReviews, CharterHistoryDialog, YachtCommission } from "./CharterHistory";

type YachtCard = Yacht & {
  header?: ReactNode;
  openYachtDetails?: () => void;
  openChangeDetails: () => void;
  onQuickAdd?: () => void;
  offerCurrencyCode?: Currency["code"];
  showBreakdown?: boolean;
  tripLength?: number;
  charterFlag?: CharterFlag;
  commission?: YachtCommission;
};

export const YachtCard = (props: YachtCard) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const includes = [...(props.amenities || [])].filter((am) => am.included);
  const obligatories = [...(props.amenities || [])].filter((am) => am.paid);
  const { offerYachts, currencies } = useOffersStore();

  // Helper to get the website URL from runtime config
  function getWebsiteUrl(): string {
    try {
      const { WEBSITE_URL } = getConfig();
      return WEBSITE_URL;
    } catch (err) {
      console.error("Config not loaded yet", err);
      return ""; // fallback or throw error if preferred
    }
  }

  const renderCurrencyPrice = (amount: number) => {
    if (!currencies?.length) return null;
    const offerCurrencyCode = props.offerCurrencyCode ?? "EUR";
    const yachtCurrencyCode = "EUR"; // always EUR
    const yachtCurrency = currencies?.find((c) => c.code === yachtCurrencyCode);
    const offerCurrency = currencies?.find((c) => c.code === offerCurrencyCode);
    const price = calculateCurrencyPrice(offerCurrency!, yachtCurrency!, amount ?? 0);
    return new Intl.NumberFormat("en-EN", { style: "currency", currency: offerCurrency?.code }).format(price);
  };

  const { price, deposit, extrasPrice } = useMemo(
    () => ({
      price: renderCurrencyPrice(props.pricing[0]?.clientPrice ?? 0),
      deposit: renderCurrencyPrice(props.deposit),
      extrasPrice: renderCurrencyPrice(props.obligatoryExtrasPrice),
    }),
    [props.pricing[0]?.clientPrice ?? 0, props.deposit, props.obligatoryExtrasPrice, currencies, props.offerCurrencyCode],
  );

  const priceBreakdown = useMemo(() => {
    const { listPrice, clientPrice, discount } = props.pricing[0];
    const hasDiscount = clientPrice !== listPrice;
    const discountAmount = listPrice - clientPrice;

    return {
      listPrice: renderCurrencyPrice(listPrice),
      clientPrice: renderCurrencyPrice(clientPrice),
      discountAmount: renderCurrencyPrice(discountAmount),
      hasDiscount,
      discount: discount.toFixed(2) + "%",
    };
  }, [props.pricing, currencies, props.offerCurrencyCode]);

  // VAT & APA (show both): for exclusive/plus-expenses yachts (e.g. Greek
  // Yachtfolio) show the base charter fee plus VAT/APA broken out and an
  // estimated all-in. VAT/APA arrive as percentages of the charter fee.
  const p0 = props.pricing[0];
  const vatPct = p0?.vat ?? 0;
  const apaPct = p0?.apa ?? 0;
  const isInclusive = !!p0?.inclusive;
  const baseClient = p0?.clientPrice ?? 0;
  const vatAmt = isInclusive ? 0 : (baseClient * vatPct) / 100;
  const apaAmt = isInclusive ? 0 : (baseClient * apaPct) / 100;
  const showTaxes = !isInclusive && (vatPct > 0 || apaPct > 0);
  const allInEst = baseClient + vatAmt + apaAmt;

  let optionLabel: string | undefined;
  if (props.isOnOption) {
    optionLabel = "On option for period";
  }
  if (props.reservations.length > 0) {
    const r = props.reservations[0];
    optionLabel = `On option ` + (r.ownedByHelm ? "for Helm " : "") + "until " + r.expirationDate.split("T")[0];
  }

  return (
    <div className="flex flex-col p-3.5 border rounded-lg border-blue-100">
      {props.header}
      <div className="flex gap-4">
        <span className="relative shrink-0">
          <img
            width={200}
            height={128}
            className="h-[128px] w-[200px] object-cover rounded-md"
            src={props.media?.[0]}
            alt={props.name}
          />
          {!!props.charterFlag?.count && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setHistoryOpen(true);
              }}
              className="absolute top-2 left-2 inline-flex items-center gap-1 bg-white/95 rounded-full px-2 py-0.5 text-xs font-semibold shadow hover:bg-white"
              title="Charter history"
            >
              ❤ {props.charterFlag.yachtCount || props.charterFlag.count}×
              {props.charterFlag.avgRating != null && <> · ★{props.charterFlag.avgRating.toFixed(1)}</>}
            </button>
          )}
          {historyOpen && props.charterFlag && (
            <CharterHistoryDialog
              yachtId={props.charterFlag.yachtId}
              yachtName={props.name}
              open={historyOpen}
              setOpen={setHistoryOpen}
            />
          )}
        </span>

        <div className="flex flex-col items-start min-w-0 grow">
          {(props.apiStatus !== "synced" || optionLabel) && (
            <YachtLabel optionLabel={optionLabel} apiDateUpdated={props.apiDateUpdated} apiStatus={props.apiStatus} />
          )}
          <div className="flex items-center gap-2">
            <a
              target="_blank"
              rel="noreferrer"
              href={`${getWebsiteUrl()}/yacht/${props.slug}`}
              className="inline-flex items-center font-semibold text-[15px] hover:underline"
            >
              “{props.name}”
              <ExternalLink className="ml-1.5 text-slate-400" size={14} />
            </a>
            {!!props.provider && (
              <span className="text-[11px] font-medium bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">
                {props.provider}
              </span>
            )}
          </div>
          <span className="text-sm text-slate-500 mt-0.5">
            {props.model} | {props.year}
          </span>
          <span className="text-sm mt-0.5">
            {[
              props.maxPeople && `${props.maxPeople} people`,
              props.cabins && `${props.cabins} cabins`,
              props.bathrooms && `${props.bathrooms} bathrooms`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
          <div className="mt-1.5">
            <span className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">Operator</span>
            <span className="text-sm ml-1.5">
              {props.operator} · <b>{props.operatorRating}/10</b>
            </span>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">Brand</span>
            <span className="text-sm ml-1.5">
              {[props.visibleInAnchor && "Anchor", props.visibleInHelm && "Helm"].filter(Boolean).join(", ")}
            </span>
          </div>
        </div>

        <div className="flex flex-col justify-between items-end shrink-0">
          <span className="flex flex-col items-end text-sm">
            {props.showBreakdown ? (
              <>
                <span className="text-xs text-slate-400 line-through">{priceBreakdown.listPrice}</span>
                {priceBreakdown.hasDiscount && (
                  <span className="text-xs text-green-600">
                    Discount: {priceBreakdown.discountAmount} ({priceBreakdown.discount})
                  </span>
                )}
                <span className="flex items-baseline gap-1.5">
                  <span className="text-xs text-slate-500">Charter price</span>
                  <span className="text-lg font-bold">{priceBreakdown.clientPrice}</span>
                </span>
              </>
            ) : (
              <span className="flex items-baseline gap-1.5">
                <span className="text-xs text-slate-500">From</span>
                <span className="text-lg font-bold">{price}</span>
                <span className="text-xs text-slate-500">/wk</span>
              </span>
            )}
            {props.deposit !== 0 && <span className="text-xs text-slate-500">Deposit: {deposit}</span>}
            {props.obligatoryExtrasPrice !== 0 && (
              <span className="text-xs text-slate-500">Obligatory extras: {extrasPrice}</span>
            )}
            {showTaxes && (
              <>
                <span className="text-xs text-slate-500">
                  + VAT ({vatPct}%): {renderCurrencyPrice(vatAmt)}
                </span>
                <span className="text-xs text-slate-500">
                  + APA ({apaPct}%): {renderCurrencyPrice(apaAmt)}
                </span>
                <span className="text-xs font-semibold text-slate-700">All-in est. {renderCurrencyPrice(allInEst)}</span>
              </>
            )}
            {isInclusive && <span className="text-xs font-medium text-emerald-600">All-inclusive</span>}
            {!!props.commission?.commission && (
              <span className="text-xs font-medium text-emerald-700 mt-0.5">
                Commission {props.commission.commission_type === "percentage"
                  ? `${props.commission.commission}% · ${renderCurrencyPrice(
                      ((props.pricing[0]?.clientPrice ?? 0) * props.commission.commission) / 100,
                    )}`
                  : renderCurrencyPrice(props.commission.commission)}
              </span>
            )}
          </span>
          <Button
            onClick={(e) => {
              e.preventDefault();
              (props.onQuickAdd ?? props.openChangeDetails)();
            }}
            variant="default"
            size="sm"
            className="mt-3"
          >
            Add to offer <Plus className="ml-2" size={15} />
          </Button>
        </div>
      </div>
      <Separator className="my-3" />
      <div className="flex">
        <div className="flex gap-x-8">
          <div className="flex flex-col w-[186px]">
            <span className="text-xs text-slate-500 font-medium">Check-in</span>
            <span className="text-base font-medium">{props.startLocation}</span>
            <span className="text-base font-medium">{formatTime(props.startTime)}</span>
          </div>
          <div className="flex flex-col w-[186px]">
            <span className="text-xs text-slate-500 font-medium">Check-out</span>
            <span className="text-base font-medium">{props.endLocation}</span>
            <span className="text-base font-medium">{formatTime(props.endTime)}</span>
          </div>
        </div>
        <Separator orientation="vertical" className="mx-6 h-auto" />
        <div className="flex flex-col gap-y-2.5 min-w-0">
          {!!includes?.length && (
            <div className="flex flex-col gap-y-1.5">
              <span className="text-xs text-slate-500 font-medium">Main features</span>
              <ul className="flex items-center flex-wrap gap-1.5">
                {includes?.slice(0, 5)?.map(
                  (feature, i) =>
                    feature.text && (
                      <li key={i} className="leading-3 rounded-full py-1 px-2.5 bg-slate-100 font-medium text-xs">
                        {feature.text}
                      </li>
                    ),
                )}
                {!!includes?.slice(5).length && (
                  <li className="leading-3 rounded-full py-1 px-2.5 bg-slate-100 font-medium text-xs">
                    + {includes?.slice(5)?.length}
                  </li>
                )}
              </ul>
            </div>
          )}
          {!!obligatories?.length && (
            <div className="flex flex-col gap-y-1.5">
              <span className="text-xs text-slate-500 font-medium">Obligatory extras</span>
              <ul className="flex items-center flex-wrap gap-1.5">
                {obligatories?.slice(0, 5).map(
                  (feature, i) =>
                    feature.text && (
                      <li key={i} className="leading-3 rounded-full py-1 px-2.5 bg-slate-100 font-medium text-xs">
                        {feature.text}
                      </li>
                    ),
                )}
                {!!obligatories?.slice(5).length && (
                  <li className="leading-3 rounded-full py-1 px-2.5 bg-slate-100 font-medium text-xs">
                    + {obligatories?.slice(5)?.length}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
      {props.brokerNotes !== "" && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3.5 py-2.5">
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-800">
            Broker note
            <span className="font-normal text-amber-700/80 bg-amber-100 rounded px-1.5 py-0.5">
              internal — not sent to client
            </span>
          </div>
          <p className="mt-1 text-sm text-amber-900 break-words whitespace-pre-wrap">{props.brokerNotes}</p>
        </div>
      )}
      <CardReviews flag={props.charterFlag} yachtId={props.id} />
    </div>
  );
};

function formatTime(t: string) {
  const d = new Date(t);
  const dd = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
  return format(dd, "dd MMM HH:mm");
}
